import { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import api from 'services/api'
import { trackEvent } from 'services/analytics'
import CityModal from 'components/cityModal'
import { ModelCity } from 'models/ModelCity'

type DestinationInsights = {
  routeCount: number
  averageDays: number | null
  commonDays: number | null
  topCompanions: Array<{
    name: string
    itineraryCount: number
    sharePct: number
  }>
  sampleSize: number
  updatedAt?: string | null
}

type DestinationPageData = {
  destinationKey: string
  sourceName: string
  displayName: string
  latitude: number
  longitude: number
  countryCode?: string | null
  countryName?: string | null
  slug: string
  featured: boolean
  description?: string | null
  seoTitle?: string | null
  seoDescription?: string | null
  cityId?: string | null
  updatedAt?: string | null
}

const ensureMeta = (
  selector: string,
  attributes: Record<string, string>,
  content?: string
) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null

  if (!element) {
    element = document.createElement(
      attributes.rel ? 'link' : 'meta'
    ) as HTMLMetaElement
    Object.entries(attributes).forEach(([key, value]) =>
      element?.setAttribute(key, value)
    )
    document.head.appendChild(element)
  }

  if (content !== undefined) {
    element.setAttribute('content', content)
  }

  return element
}

const DestinationSeo = () => {
  const history = useHistory()
  const { slug } = useParams<{ slug: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['destination-page', slug],
    queryFn: async () => {
      const response = await api.get<DestinationPageData>(
        `/cities/slug/${encodeURIComponent(slug)}`
      )
      return response.data
    },
    staleTime: 5 * 60 * 1000
  })

  const { data: destinationInsights } = useQuery({
    queryKey: ['destination-insights-schema', data?.cityId],
    queryFn: async () => {
      const response = await api.get<DestinationInsights>(
        `/cities/${data?.cityId}/insights`
      )
      return response.data
    },
    enabled: Boolean(data?.cityId),
    staleTime: 5 * 60 * 1000
  })

  useEffect(() => {
    if (!data?.slug || data.slug === slug) return
    history.replace(`/destinos/${data.slug}`)
  }, [data?.slug, history, slug])

  useEffect(() => {
    if (!data) return

    const title =
      data.seoTitle ||
      `${data.displayName}: roteiros, dicas e o que fazer | RedSterna`
    const description =
      data.seoDescription ||
      `Descubra ${data.displayName} com roteiros, destinos relacionados e informações para planejar sua viagem.`
    const canonical = `https://redsterna.com.br/destinos/${data.slug}`

    document.title = title

    ensureMeta('meta[name="description"]', { name: 'description' }, description)
    ensureMeta('meta[property="og:title"]', { property: 'og:title' }, title)
    ensureMeta(
      'meta[property="og:description"]',
      { property: 'og:description' },
      description
    )
    ensureMeta('meta[property="og:url"]', { property: 'og:url' }, canonical)
    ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title)
    ensureMeta(
      'meta[name="twitter:description"]',
      { name: 'twitter:description' },
      description
    )

    let canonicalElement = document.head.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null

    if (!canonicalElement) {
      canonicalElement = document.createElement('link')
      canonicalElement.rel = 'canonical'
      document.head.appendChild(canonicalElement)
    }
    canonicalElement.href = canonical

    const scriptId = 'redsterna-destination-structured-data'
    document.getElementById(scriptId)?.remove()
    const script = document.createElement('script')
    script.id = scriptId
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': canonical + '#webpage',
          url: canonical,
          name: title,
          description,
          inLanguage: 'pt-BR',
          dateModified: data.updatedAt || undefined,
          isPartOf: {
            '@id': 'https://redsterna.com.br/#website'
          },
          publisher: {
            '@id': 'https://redsterna.com.br/#organization'
          },
          mainEntity: {
            '@id': canonical + '#destination'
          }
        },
        {
          '@type': 'TouristDestination',
          '@id': canonical + '#destination',
          name: data.displayName,
          description,
          url: canonical,
          geo: {
            '@type': 'GeoCoordinates',
            latitude: data.latitude,
            longitude: data.longitude
          }
        },
        ...(destinationInsights && destinationInsights.routeCount > 0
          ? [
              {
                '@type': 'Dataset',
                license: 'https://redsterna.com.br/licenca-de-dados/v1',
                usageInfo: 'https://redsterna.com.br/licenca-de-dados/v1',
                '@id': canonical + '#route-insights',
                name: `Dados agregados de roteiros para ${data.displayName}`,
                description: `Estatísticas agregadas a partir de ${destinationInsights.routeCount} roteiros públicos disponíveis na RedSterna.`,
                inLanguage: 'pt-BR',
                dateModified:
                  destinationInsights.updatedAt || data.updatedAt || undefined,
                creator: {
                  '@id': 'https://redsterna.com.br/#organization'
                },
                includedInDataCatalog: {
                  '@type': 'DataCatalog',
                  name: 'RedSterna'
                },
                measurementTechnique:
                  'Agregação de roteiros públicos disponíveis na RedSterna',
                variableMeasured: [
                  {
                    '@type': 'PropertyValue',
                    name: 'Quantidade de roteiros públicos',
                    value: destinationInsights.routeCount
                  },
                  ...(destinationInsights.averageDays === null
                    ? []
                    : [
                        {
                          '@type': 'PropertyValue',
                          name: 'Média de dias no destino',
                          value: destinationInsights.averageDays,
                          unitText: 'dias'
                        }
                      ]),
                  ...(destinationInsights.commonDays === null
                    ? []
                    : [
                        {
                          '@type': 'PropertyValue',
                          name: 'Duração mais comum no destino',
                          value: destinationInsights.commonDays,
                          unitText: 'dias'
                        }
                      ])
                ],
                keywords: destinationInsights.topCompanions.map(
                  (item) => item.name
                )
              }
            ]
          : []),
        {
          '@type': 'BreadcrumbList',
          '@id': canonical + '#breadcrumb',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'RedSterna',
              item: 'https://redsterna.com.br/'
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Destinos',
              item: 'https://redsterna.com.br/destinos'
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: data.displayName,
              item: canonical
            }
          ]
        }
      ]
    })
    document.head.appendChild(script)

    trackEvent({
      eventType: 'destination_view',
      entityType: 'destination',
      entityId: data.destinationKey,
      value: data.displayName,
      metadata: { source: 'seo_page', slug: data.slug }
    })

    return () => {
      document.getElementById(scriptId)?.remove()
    }
  }, [data, destinationInsights])

  if (isLoading) {
    return <div style={{ padding: 40 }}>Carregando destino...</div>
  }

  if (
    isError ||
    !data?.slug ||
    !data?.destinationKey ||
    !data?.displayName ||
    !Number.isFinite(Number(data.latitude)) ||
    !Number.isFinite(Number(data.longitude)) ||
    (Number(data.latitude) === 0 && Number(data.longitude) === 0)
  ) {
    return (
      <div style={{ padding: 40 }}>
        <h1>Destino não encontrado</h1>
        <button type="button" onClick={() => history.push('/destinos')}>
          Ver destinos
        </button>
      </div>
    )
  }

  const city = {
    // Fallback identity for destinations not present in the legacy city table.
    id: data.cityId || data.destinationKey,
    name: data.displayName,
    latitude: data.latitude,
    longitude: data.longitude,
    description: data.description || undefined
  } as ModelCity

  return (
    <CityModal
      isOpen
      city={city}
      closeModal={() => history.push('/destinos')}
      shouldCloseOnOverlayClick={false}
    />
  )
}

export default DestinationSeo
